import React from "react";
import { withStyles, makeStyles } from "@material-ui/core/styles";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TablePagination from "@material-ui/core/TablePagination";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import { Button, Typography } from "@material-ui/core";
import Paper from "@material-ui/core/Paper";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import CancelIcon from "@material-ui/icons/Cancel";
import GroupClients from "../Modals/GroupClients";
import dayjs from "dayjs";
import Purchase from "../Modals/Purchase";
import UserPurchaseCard from "./UserPurchaseCard";
import axios from "axios";
import ClipLoader from "react-spinners/FadeLoader";
import { css } from "@emotion/react";

const StyledTableCell = withStyles((theme) => ({
  head: {
    backgroundColor: "#bb3d3d",
    color: theme.palette.common.white,
  },
  body: {
    fontSize: 14,
  },
}))(TableCell);

const StyledTableRow = withStyles((theme) => ({
  root: {
    "&:nth-of-type(odd)": {
      backgroundColor: theme.palette.action.hover,
    },
  },
}))(TableRow);

const useStyles = makeStyles({
  table: {
    minWidth: 700,
  },
});

interface OrderRow {
  date_paid?: string;
  id?: React.Key;
  total?: string | number;
  discount_total?: string | number;
  currency_symbol?: string;
  line_items?: React.ComponentProps<typeof Purchase>["order"]["line_items"];
}

interface UserPurchasesProps {
  isMobile?: boolean;
  userId?: string;
}

export default function CustomizedTables(props: UserPurchasesProps): React.ReactElement {
  const override = css`
    display: block;
    margin: 0 auto;
    border-color: red;
  `;
  const classes = useStyles();
  const [open, setOpen] = React.useState(false);
  const [orders, setOrders] = React.useState<OrderRow[]>([]);
  const [order, setOrder] = React.useState<OrderRow>({});
  const [page, setPage] = React.useState(0);
  const [loading, setLoading] = React.useState(false);
  const [groupName, setGroupName] = React.useState<string>("");
  const [rowsPerPage, setRowsPerPage] = React.useState(7);

  React.useEffect(() => {
    if (props.userId) {
      setLoading(true);
      axios
        .post<OrderRow[]>("/api/user/get-all-orders-from-id", {
          id: props.userId,
        })
        .then((res) => {
          setOrders(res.data);
          setLoading(false);
        });
    }
  }, [props.userId]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };
  return loading ? (
    <React.Fragment>
      <ClipLoader
        color={"#398bd6"}
        loading={loading}
        css={override}
      />{" "}
      <Typography align='center' style={{ marginTop: 10, color: "#398bd6" }}>
        Retrieving purchase data...
      </Typography>
    </React.Fragment>
  ) : props.isMobile ? (
    <div>
      {orders
        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
        .map((row) => (
          <UserPurchaseCard
            key={row.id as React.Key}
            order={row}
            onView={() => {
              setOrder(row);
              setOpen(true);
            }}
          />
        ))}
      <TablePagination
        rowsPerPageOptions={[5, 7, 14, 50]}
        style={{ marginTop: 16, background: "#fafafa", borderRadius: 4 }}
        component='div'
        count={orders.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
      <Purchase order={order} open={open} setOpen={setOpen} isMobile={props.isMobile} />
    </div>
  ) : (
    <div>
      <Paper className={(classes as { root?: string }).root} variant='outlined'>
        <TableContainer style={{ maxHeight: 440 }}>
          <Table className={classes.table} aria-label='customized table'>
            <TableHead>
              <TableRow>
                <StyledTableCell align='center' style={{}}>
                  Order ID
                </StyledTableCell>
                <StyledTableCell align='center' style={{}}>
                  Order Date
                </StyledTableCell>
                <StyledTableCell align='center' style={{ maxWidth: 160 }}>
                  Number of Items
                </StyledTableCell>
                <StyledTableCell align='center' style={{}}>
                  Details
                </StyledTableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {orders
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row) => (
                  <StyledTableRow key={row.id as React.Key}>
                    <StyledTableCell
                      align='center'
                      component='th'
                      scope='row'
                      style={{
                        borderRight: "1px solid #e3e3e3",
                        paddingTop: 10,
                        paddingBottom: 10,
                      }}
                    >
                      {row.id}
                    </StyledTableCell>
                    <StyledTableCell
                      align='center'
                      style={{
                        borderRight: "1px solid #e3e3e3",
                        paddingTop: 10,
                        paddingBottom: 10,
                      }}
                    >
                      {dayjs(row.date_paid).format("DD/MM/YYYY")}
                    </StyledTableCell>
                    <StyledTableCell
                      align='center'
                      style={{
                        maxWidth: 160,
                        borderRight: "1px solid #e3e3e3",
                        paddingTop: 10,
                        paddingBottom: 10,
                      }}
                    >
                      {row.line_items!.length}
                    </StyledTableCell>

                    <StyledTableCell
                      align='center'
                      style={{
                        borderRight: "1px solid #e3e3e3",
                        paddingTop: 10,
                        paddingBottom: 10,
                      }}
                    >
                      {" "}
                      <Button
                        variant='outlined'
                        size='small'
                        onClick={() => {
                          setOrder(row);

                          setOpen(true);
                        }}
                      >
                        View
                      </Button>
                    </StyledTableCell>
                  </StyledTableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 7, 14, 50]}
          style={{ borderTop: "1px solid #e3e3e3", background: "#fafafa" }}
          component='div'
          count={orders.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />{" "}
      </Paper>
      <Purchase order={order} open={open} setOpen={setOpen} isMobile={props.isMobile} />
    </div>
  );
}
